import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/status";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function NotificationsBell({ compact }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch (e) { console.error("Failed to load notifications", e); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const markRead = async () => {
    await api.post("/notifications/mark-read");
    setData((d) => ({ ...d, unread: 0 }));
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && data.unread) markRead(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className={`relative flex items-center gap-3 rounded-sm text-[#6b6a68] hover:text-[#232826] hover:bg-[#efece5] transition-colors ${compact ? "p-2" : "w-full px-4 py-3 text-sm"}`}>
          <Bell size={18} strokeWidth={1.8} />
          {!compact && <span className="font-medium">Notifications</span>}
          {data.unread > 0 && (
            <span data-testid="notifications-unread" className={`absolute ${compact ? "top-0.5 right-0.5" : "right-4 top-1/2 -translate-y-1/2"} min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center`}>
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="right" className="w-96 p-0 bg-white border-[#e6e5df] rounded-sm" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-[#e6e5df] flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono">Activity feed</div>
          <CheckCheck size={14} className="text-[#a39f98]" />
        </div>
        <div className="max-h-96 overflow-y-auto divide-y divide-[#e6e5df]">
          {data.items.length === 0 && <div className="p-6 text-sm text-[#a39f98] text-center">Nothing yet.</div>}
          {data.items.map((n) => (
            <Link key={n.id} to={`/rework/${n.rework_id}`} onClick={() => setOpen(false)} data-testid={`notification-${n.id}`}
              className="block px-4 py-3 hover:bg-[#efece5]/70 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className={`text-sm font-medium ${n.audience === "install" ? "text-rose-800" : "text-[#232826]"}`}>{n.title}</div>
                {n.teams_sent && <span className="text-[9px] uppercase tracking-widest text-sky-700 border border-sky-200 bg-sky-50 px-1.5 rounded-sm">Teams</span>}
              </div>
              <div className="text-xs text-[#6b6a68] mt-0.5 line-clamp-2">{n.message}</div>
              <div className="text-[10px] font-mono text-[#a39f98] mt-1">{fmtDateTime(n.created_at)}</div>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
